import { useState } from "react"

import { Divider, Flex, Modal, Popover, Tag, Tooltip, Typography } from "antd"
import cls from "classname"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import ButtonComponent from "../../../../Components/Button"
import InfoTooltip from "../../../../Components/InfoTooltip/InfoTooltip"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import { dateFormat } from "../../../../Utils/utils"

import styles from "./AutomationStatusButton.module.less"

const { Title, Text } = Typography

const processorImage = (serviceName) =>
  Images[`integration_${serviceName}`] || Images.integration_missing

const IntegrationIconStack = ({ processors }) => (
  <Flex className={styles.iconStack}>
    {processors.map((processor, index) => (
      <span
        key={processor.service_name}
        className={styles.stackedIcon}
        style={{ zIndex: processors.length - index }}
      >
        <img
          src={processorImage(processor.service_name)}
          width={14}
          height={14}
          alt={processor.display_name}
        />
      </span>
    ))}
  </Flex>
)

const AutomationBadge = ({ processors, variant = "active" }) => {
  const { t } = useTranslation()
  const isProcessing = variant === "processing"
  return (
    <Flex align="center" gap={8}>
      <IntegrationIconStack processors={processors} />
      <Text>{t("Automation")}</Text>
      <Tag color={isProcessing ? "processing" : "success"} bordered={false} style={{ margin: 0 }}>
        {isProcessing ? t("Processing") : t("Active")}
      </Tag>
    </Flex>
  )
}

// CHA1-2308: a row needs the merchant's confirmation once an admin has sent an access request for
// it and the merchant hasn't clicked "confirm" yet (and the request hasn't expired in the meantime).
const needsMerchantConfirmation = (processor) =>
  !!processor.access_requested_at &&
  !processor.merchant_confirmed_at &&
  !processor.is_access_request_expired

export const AutomationStatusButton = ({
  processors = [],
  isStripeOnly = false,
  variant = "active",
  onConfirmAccess,
  confirmFetching = false,
  onTurnOff,
  turnOffFetching = false
}) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const [open, setOpen] = useState(false)
  const [turnOffModalOpen, setTurnOffModalOpen] = useState(false)

  // CHA1-3088: with Stripe as the only connected processor, access is automatically Active and
  // there's nothing to turn off - the button is a plain status badge with no dropdown.
  if (isStripeOnly) {
    return (
      <ButtonComponent className={styles.statusButton}>
        <AutomationBadge processors={processors} />
      </ButtonComponent>
    )
  }

  const turnOffModal = (
    <Modal
      open={turnOffModalOpen}
      onCancel={() => setTurnOffModalOpen(false)}
      centered={!isMobile}
      width={442}
      title={null}
      footer={null}
      destroyOnClose
      classNames={{ content: "clear-bg" }}
      closeIcon={<Icon type="cross_small" />}
    >
      <Flex vertical gap={16}>
        <Icon type="warning_orange" style={{ fontSize: 40 }} />

        <Title level={5} style={{ margin: 0 }}>
          {t("Turn off Credit Automation?")}
        </Title>

        <Text type="secondary">
          {t("Chargeback will stop handling your system credit requests automatically.")}
        </Text>

        <Text className={styles.warningText}>
          {t(
            "Turning off Credit Automation does not remove Chargeback Admin's access. To revoke access, remove Chargeback Admin manually from your payment processor account."
          )}
        </Text>

        <div>
          <Text strong>{t("After you turn it off:")}</Text>
          <Flex vertical gap={4} className={styles.turnOffList}>
            <Flex gap={8}>
              <Text>•</Text>
              <Text>
                {t("New system credit requests will no longer be processed automatically.")}
              </Text>
            </Flex>
            <Flex gap={8}>
              <Text>•</Text>
              <Text>{t("Automatic Credit Request emails notifications will resume.")}</Text>
            </Flex>
          </Flex>
        </div>

        <Text type="secondary">
          {t(
            "You can enable Credit Automation again at any time by submitting a new automation request."
          )}
        </Text>

        <Flex gap={12} justify="end" className={styles.footer}>
          <ButtonComponent size="large" onClick={() => setTurnOffModalOpen(false)}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent
            size="large"
            type="primary"
            loading={turnOffFetching}
            onClick={async () => {
              try {
                await onTurnOff?.().unwrap()
                setTurnOffModalOpen(false)
              } catch {
                // error notification already handled by handleApiError inside the thunk
              }
            }}
          >
            {t("Turn off")}
          </ButtonComponent>
        </Flex>
      </Flex>
    </Modal>
  )

  const isProcessing = variant === "processing"
  const pendingConfirmations = processors.filter(needsMerchantConfirmation)
  const latestSyncAt = [...processors]
    .map((processor) => processor.last_access_check_at)
    .filter(Boolean)
    .sort()
    .at(-1)

  const content = (
    <Flex vertical gap={10} className={styles.panel}>
      <Text type="secondary">{t("Automation details")}</Text>

      <Tooltip title={processors.map((processor) => processor.display_name).join(", ")}>
        <Flex gap={0} style={{ width: "fit-content" }}>
          {processors.map((processor) => (
            <span key={processor.service_name} className={styles.processorIcon}>
              <img
                src={processorImage(processor.service_name)}
                width={20}
                height={20}
                alt={processor.display_name}
              />
            </span>
          ))}
        </Flex>
      </Tooltip>

      <Flex align="center" gap={8}>
        <Text strong>{t("_count_ connected accounts", { count: processors.length })}</Text>
        <InfoTooltip title={t("All connected payment processors used for automated credits")} />
      </Flex>

      {isProcessing ? (
        // CHA1-2308: while a processor's access request is awaiting the merchant's confirmation,
        // that's actionable here - show a confirm button per row. Otherwise it's just system/admin
        // work in progress, nothing for the merchant to do yet.
        pendingConfirmations.length ? (
          <Flex vertical gap={8}>
            {pendingConfirmations.map((processor) => (
              <Flex key={processor.service_name} align="center" justify="space-between" gap={8}>
                <Text type="secondary">
                  {t("Confirm access to _name_", { name: processor.display_name })}
                </Text>
                <ButtonComponent
                  size="small"
                  loading={confirmFetching}
                  onClick={() => onConfirmAccess?.(processor.service_name)}
                >
                  {t("I've granted access")}
                </ButtonComponent>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Text type="secondary">{t("Verifying your payment processor access...")}</Text>
        )
      ) : (
        <>
          <Flex vertical gap={4}>
            <Text type="success" strong style={{ color: "#35BD29" }}>
              {t("Connected")}
            </Text>
            {latestSyncAt && (
              <Text type="secondary">
                {t("Last sync: _time_", {
                  time: dayjs(latestSyncAt).format(`${dateFormat} h:mm A`)
                })}
              </Text>
            )}
          </Flex>

          <Divider style={{ margin: 0 }} />

          <ButtonComponent
            type="text"
            danger
            icon={<Icon type="circle_x" />}
            className={styles.turnOffButton}
            onClick={() => {
              setOpen(false)
              setTurnOffModalOpen(true)
            }}
          >
            {t("Turn off")}
          </ButtonComponent>
        </>
      )}
    </Flex>
  )

  return (
    <>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="bottomRight"
        content={content}
        getPopupContainer={() => document.getElementById("main-layout")}
      >
        <ButtonComponent className={cls(styles.statusButton, { [styles.open]: open })}>
          <Flex align="center" gap={8}>
            <AutomationBadge processors={processors} variant={variant} />
            <Icon type="chevron_down_small" className={styles.chevron} />
          </Flex>
        </ButtonComponent>
      </Popover>
      {turnOffModal}
    </>
  )
}

export default AutomationStatusButton
